import React, { useEffect, useState } from 'react';
import { Card, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { KnowledgeArticleBatchImportResponse, KnowledgeArticleCategory } from '../types';
import { knowledgeBaseApi } from '../services/api';
import useAlert from '../hooks/useAlert';
import { KB_IMPORT_ACCEPT, validateKbImportFile } from '../utils/uploadValidation';

const KnowledgeArticleImport: React.FC = () => {
  const [categories, setCategories] = useState<KnowledgeArticleCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string>('');
  const [isExcelFile, setIsExcelFile] = useState(false);
  const [batchResult, setBatchResult] = useState<KnowledgeArticleBatchImportResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showAlert, AlertComponent } = useAlert();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await knowledgeBaseApi.getCategories();
      setCategories(data);
      if (data.length > 0 && !categoryId) {
        setCategoryId(data[0].id);
      }
    } catch (error: any) {
      console.error('Erro ao carregar categorias:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar categorias', variant: 'danger' });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setBatchResult(null);
    if (selectedFile) {
      const fileError = validateKbImportFile(selectedFile);
      if (fileError) {
        showAlert({ title: 'Arquivo inválido', message: fileError, variant: 'warning' });
        e.target.value = '';
        setFile(null);
        setPreview('');
        setIsExcelFile(false);
        return;
      }
      const ext = selectedFile.name.toLowerCase().split('.').pop();
      const excelSelected = ext === 'xlsx' || ext === 'xls';
      setIsExcelFile(excelSelected);

      // Para Word/Excel, mostrar nome do arquivo.
      setPreview(`Arquivo selecionado: ${selectedFile.name}`);
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      showAlert({ title: 'Aviso', message: 'Selecione um arquivo', variant: 'warning' });
      return;
    }
    if (!isExcelFile && !categoryId) {
      showAlert({ title: 'Aviso', message: 'Selecione uma categoria para importar arquivo Word', variant: 'warning' });
      return;
    }

    try {
      setLoading(true);
      if (isExcelFile) {
        const result = await knowledgeBaseApi.importArticlesBatch(file);
        setBatchResult(result);
        showAlert({
          title: result.success ? 'Sucesso' : 'Importacao concluida com alertas',
          message: `Artigos criados: ${result.created}. Erros: ${result.errors}. Categorias criadas: ${result.created_categories}.`,
          variant: result.success ? 'success' : 'warning',
        });
      } else {
        const article = await knowledgeBaseApi.importArticle(Number(categoryId), file);
        showAlert({ title: 'Sucesso', message: 'Artigo importado com sucesso', variant: 'success' });
        navigate(`/knowledge-base/${article.id}`);
      }
    } catch (error: any) {
      console.error('Erro ao importar artigo:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao importar artigo',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <AlertComponent />
      <Row className="mb-3">
        <Col>
          <h2>Importar Artigo</h2>
        </Col>
        <Col xs="auto">
          <Button variant="outline-secondary" onClick={() => navigate('/knowledge-base')}>
            Cancelar
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            {!isExcelFile && (
              <Form.Group className="mb-3">
                <Form.Label>Categoria *</Form.Label>
                <Form.Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
                  required={!isExcelFile}
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Arquivo *</Form.Label>
              <Form.Control
                type="file"
                accept={KB_IMPORT_ACCEPT}
                onChange={handleFileChange}
                required
              />
              <Form.Text className="text-muted">
                Formatos suportados: .docx, .doc, .xlsx, .xls.
                Para Excel, a categoria vem da planilha e nao precisa ser selecionada aqui.
              </Form.Text>
            </Form.Group>

            {preview && (
              <Alert variant="info" className="mb-3">
                <Alert.Heading>Preview do Conteúdo</Alert.Heading>
                <pre style={{ whiteSpace: 'pre-wrap', maxHeight: '300px', overflow: 'auto' }}>
                  {preview}
                </pre>
              </Alert>
            )}

            {batchResult && (
              <Alert variant={batchResult.success ? 'success' : 'warning'} className="mb-3">
                <Alert.Heading>Resumo da Importacao em Lote</Alert.Heading>
                <div>Total de linhas: {batchResult.total_rows}</div>
                <div>Artigos criados: {batchResult.created}</div>
                <div>Categorias criadas: {batchResult.created_categories}</div>
                <div>Erros: {batchResult.errors}</div>
                {batchResult.error_details.length > 0 && (
                  <div className="mt-2">
                    <strong>Primeiros erros:</strong>
                    <ul className="mb-0 mt-1">
                      {batchResult.error_details.slice(0, 10).map((item, index) => (
                        <li key={`${item.row}-${index}`}>
                          Linha {item.row}: {item.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Alert>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => navigate('/knowledge-base')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading || !file || (!isExcelFile && !categoryId)}>
                {loading ? 'Importando...' : 'Importar'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default KnowledgeArticleImport;
